import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, Save, Image, Type, Clock, Phone, Eye, Loader2, Plus, X, GalleryHorizontalEnd, Upload } from 'lucide-react';
import api from '../api/axios';

const VitrineConfig = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [newGalleryUrl, setNewGalleryUrl] = useState('');
  const [newGalleryCaption, setNewGalleryCaption] = useState('');
  const [uploadingGallery, setUploadingGallery] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('staffToken');
    const role = localStorage.getItem('staffRole');
    if (!token) { navigate('/staff/login'); return; }
    if (!['webmaster', 'admin'].includes(role)) { toast({ title: 'Acces refuse', variant: 'destructive' }); navigate('/staff/dashboard'); return; }
    fetchConfig();
    fetchGallery();
  }, [navigate]);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/vitrine');
      setConfig(res.data);
    } catch (e) {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const fetchGallery = async () => {
    try {
      const res = await api.get('/vitrine/gallery');
      setGallery(res.data);
    } catch (e) { /* ignore */ }
  };

  const addGalleryImage = async () => {
    if (!newGalleryUrl) return;
    try {
      await api.post('/vitrine/gallery', { url: newGalleryUrl, caption: newGalleryCaption });
      setNewGalleryUrl('');
      setNewGalleryCaption('');
      fetchGallery();
      toast({ title: 'Image ajoutee' });
    } catch (e) { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  const uploadGalleryImage = async (file) => {
    setUploadingGallery(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = res.data.url;
      await api.post('/vitrine/gallery', { url: imageUrl, caption: '' });
      fetchGallery();
      toast({ title: 'Image uploadee et ajoutee' });
    } catch (e) { toast({ title: 'Erreur upload', variant: 'destructive' }); }
    finally { setUploadingGallery(false); }
  };

  const removeGalleryImage = async (index) => {
    try {
      await api.delete(`/vitrine/gallery/${index}`);
      fetchGallery();
      toast({ title: 'Image supprimee' });
    } catch (e) { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  const saveGallery = async () => {
    try {
      await api.put('/vitrine/gallery', { images: gallery.map((g, i) => ({ url: g.url, caption: g.caption || '', order: i })) });
      toast({ title: 'Galerie sauvegardee' });
    } catch (e) { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/vitrine', config);
      toast({ title: 'Configuration sauvegardee !' });
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Impossible de sauvegarder', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const u = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));

  if (loading || !config) return <div className="flex items-center justify-center min-h-screen">Chargement...</div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white">
      <div className="bg-[#3d2817] text-white py-4 shadow-lg sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold" data-testid="vitrine-config-title">Configuration Vitrine</h1>
            <p className="text-[10px] text-gray-300">Page d'accueil du site public</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => window.open('/', '_blank')} size="sm" variant="outline" className="text-xs border-gray-500 text-gray-200 hover:text-white">
              <Eye size={14} className="mr-1" /> Voir le site
            </Button>
            <Button onClick={() => navigate('/staff/dashboard')} size="sm" className="bg-red-600 hover:bg-red-700 text-xs">
              <ArrowLeft size={14} className="mr-1" /> Dashboard
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">

        {/* Hero Section */}
        <Card className="border shadow-sm" data-testid="hero-config">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="flex items-center gap-2 text-base"><Image size={18} className="text-amber-600" /> Section Hero (Banniere)</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div>
              <Label className="text-xs">Taille du logo (px)</Label>
              <div className="flex items-center gap-4 mt-1">
                <Slider value={[config.logo_size]} onValueChange={([v]) => u('logo_size', v)} min={200} max={700} step={10} className="flex-1" />
                <span className="text-sm font-mono w-14 text-right">{config.logo_size}px</span>
              </div>
              <div className="mt-2 bg-gray-900 rounded-lg p-4 flex justify-center">
                <img src="/logo-taverne.png" alt="Preview" style={{ width: `${Math.min(config.logo_size, 400)}px` }} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Phrase d'accroche</Label>
              <Input value={config.hero_tagline} onChange={(e) => u('hero_tagline', e.target.value)} className="text-sm" data-testid="hero-tagline" />
            </div>
            <div>
              <Label className="text-xs">Image de fond (URL)</Label>
              <Input value={config.hero_image} onChange={(e) => u('hero_image', e.target.value)} className="text-xs" />
              {config.hero_image && <img src={config.hero_image} alt="Hero" className="mt-1 h-20 rounded object-cover" />}
            </div>
          </CardContent>
        </Card>

        {/* About Section */}
        <Card className="border shadow-sm" data-testid="about-config">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="flex items-center gap-2 text-base"><Type size={18} className="text-blue-600" /> Section A Propos</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div>
              <Label className="text-xs">Titre</Label>
              <Input value={config.about_title} onChange={(e) => u('about_title', e.target.value)} className="text-sm" data-testid="about-title" />
            </div>
            <div>
              <Label className="text-xs">Paragraphe 1</Label>
              <Textarea value={config.about_text1} onChange={(e) => u('about_text1', e.target.value)} rows={2} className="text-sm" />
            </div>
            <div>
              <Label className="text-xs">Paragraphe 2</Label>
              <Textarea value={config.about_text2} onChange={(e) => u('about_text2', e.target.value)} rows={2} className="text-sm" />
            </div>
            <div>
              <Label className="text-xs">Paragraphe 3</Label>
              <Textarea value={config.about_text3} onChange={(e) => u('about_text3', e.target.value)} rows={2} className="text-sm" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Image 1 (URL)</Label>
                <Input value={config.about_image1} onChange={(e) => u('about_image1', e.target.value)} className="text-[10px]" />
                {config.about_image1 && <img src={config.about_image1} alt="" className="mt-1 h-16 w-full rounded object-cover" />}
              </div>
              <div>
                <Label className="text-xs">Image 2 (URL)</Label>
                <Input value={config.about_image2} onChange={(e) => u('about_image2', e.target.value)} className="text-[10px]" />
                {config.about_image2 && <img src={config.about_image2} alt="" className="mt-1 h-16 w-full rounded object-cover" />}
              </div>
              <div>
                <Label className="text-xs">Image 3 (URL)</Label>
                <Input value={config.about_image3} onChange={(e) => u('about_image3', e.target.value)} className="text-[10px]" />
                {config.about_image3 && <img src={config.about_image3} alt="" className="mt-1 h-16 w-full rounded object-cover" />}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <Card className="border shadow-sm" data-testid="cta-config">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="flex items-center gap-2 text-base"><Phone size={18} className="text-green-600" /> Section Reservation</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Titre</Label>
                <Input value={config.cta_title} onChange={(e) => u('cta_title', e.target.value)} className="text-sm" />
              </div>
              <div>
                <Label className="text-xs">Sous-titre</Label>
                <Input value={config.cta_subtitle} onChange={(e) => u('cta_subtitle', e.target.value)} className="text-sm" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Image de fond CTA (URL)</Label>
              <Input value={config.cta_image} onChange={(e) => u('cta_image', e.target.value)} className="text-xs" />
            </div>
          </CardContent>
        </Card>

        {/* Contact & Hours */}
        <Card className="border shadow-sm" data-testid="contact-config">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="flex items-center gap-2 text-base"><Clock size={18} className="text-purple-600" /> Contact & Horaires</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Telephone</Label>
                <Input value={config.phone} onChange={(e) => u('phone', e.target.value)} className="text-sm" data-testid="config-phone" />
              </div>
              <div>
                <Label className="text-xs">Adresse</Label>
                <Input value={config.address} onChange={(e) => u('address', e.target.value)} className="text-sm" />
              </div>
              <div>
                <Label className="text-xs">Jours d'ouverture</Label>
                <Input value={config.hours_label} onChange={(e) => u('hours_label', e.target.value)} className="text-sm" />
              </div>
              <div>
                <Label className="text-xs">Horaires</Label>
                <Input value={config.hours_text} onChange={(e) => u('hours_text', e.target.value)} className="text-sm" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Jour(s) de fermeture</Label>
                <Input value={config.hours_closed} onChange={(e) => u('hours_closed', e.target.value)} className="text-sm" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Gallery Section */}
        <Card className="border-2 border-[#d4c4b0]" data-testid="gallery-section">
          <CardHeader className="bg-[#f5f0ea] py-3 px-4">
            <CardTitle className="flex items-center gap-2 text-base"><GalleryHorizontalEnd size={18} /> Galerie Photos</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            {/* Current gallery images */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {gallery.map((img, i) => (
                <div key={i} className="relative group rounded-lg overflow-hidden border" data-testid={`gallery-img-${i}`}>
                  <img src={img.url} alt={img.caption || `Photo ${i + 1}`} className="w-full h-32 object-cover" />
                  <button onClick={() => removeGalleryImage(img.order)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`gallery-del-${i}`}>
                    <X size={14} />
                  </button>
                  {img.caption && <p className="text-[10px] text-center py-1 text-gray-500">{img.caption}</p>}
                </div>
              ))}
              {gallery.length === 0 && <p className="col-span-3 text-sm text-gray-400 italic text-center py-4">Aucune photo dans la galerie</p>}
            </div>

            {/* Upload image */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              <p className="text-sm font-semibold mb-2">Ajouter une photo</p>
              <div className="flex gap-2 mb-2">
                <label className="flex items-center gap-2 px-4 py-2 bg-[#8B0000] text-white rounded-md cursor-pointer hover:bg-[#6d0000] text-sm">
                  <Upload size={16} /> {uploadingGallery ? 'Upload...' : 'Depuis le PC'}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files[0]) uploadGalleryImage(e.target.files[0]); }} data-testid="gallery-upload" />
                </label>
              </div>
              <p className="text-xs text-gray-400 mb-2">Ou ajouter par URL :</p>
              <div className="flex gap-2">
                <Input value={newGalleryUrl} onChange={e => setNewGalleryUrl(e.target.value)} placeholder="URL de l'image" className="flex-1 text-sm" data-testid="gallery-url-input" />
                <Input value={newGalleryCaption} onChange={e => setNewGalleryCaption(e.target.value)} placeholder="Legende (opt.)" className="w-32 text-sm" data-testid="gallery-caption-input" />
                <Button onClick={addGalleryImage} variant="outline" size="sm" data-testid="gallery-add-btn"><Plus size={16} /></Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Button onClick={handleSave} disabled={saving} className="w-full bg-[#2c1810] hover:bg-[#4a2c1a] text-white h-12 font-semibold text-base" data-testid="save-vitrine-btn">
          {saving ? <Loader2 size={18} className="mr-2 animate-spin" /> : <Save size={18} className="mr-2" />}
          Sauvegarder la vitrine
        </Button>
      </div>
    </div>
  );
};

export default VitrineConfig;
